import React, { useEffect } from 'react';
import { useApplicationSettingsContext } from '../../contexts/ApplicationSettingsContext';
import { usePermissionCheck } from '../../hooks/usePermissionCheck';

// Debug flag to control logging - set to false to disable logs
const enableTabsDebug = false;

interface BookingTabsProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
}

export default function BookingTabs({ activeTab, onTabChange }: BookingTabsProps) {
  const { disabledPages } = useApplicationSettingsContext();
  const { canAccess } = usePermissionCheck();

  // Check if pages are disabled in application settings or by permissions
  const isPnLDisabled = !canAccess.bookingProfitLoss;
  const isMaterialsDisabled = disabledPages.includes('materials') || !canAccess.bookingMaterials;
  const isFlatRateDisabled = disabledPages.includes('flat-rates') || !canAccess.bookingRatebook;

  if (enableTabsDebug) {
    console.log('📊 BookingTabs: Tab visibility', {
      pnl: !isPnLDisabled,
      materials: !isMaterialsDisabled,
      flatRate: !isFlatRateDisabled
    });
  }

  const tabs = [
    { id: 'overview', label: 'Overview' },
    // Only show materials tab if enabled in application settings and user has permission
    ...(isMaterialsDisabled ? [] : [{ id: 'materials', label: 'Materials' }]),
    // Only show flat rates tab if enabled in application settings and user has permission
    ...(isFlatRateDisabled ? [] : [{ id: 'flat-rates', label: 'RateBook' }]),
    // Only show profit & loss tab if user has permission
    ...(isPnLDisabled ? [] : [{ id: 'profit-loss', label: 'Profit & Loss' }])
  ];

  // If the active tab is not in the available tabs, switch to overview
  useEffect(() => {
    if (activeTab !== 'overview' && !tabs.some(tab => tab.id === activeTab)) {
      if (enableTabsDebug) console.log('⚠️ BookingTabs: Active tab not available, switching to overview');
      onTabChange('overview');
    }
  }, [activeTab, tabs, onTabChange]);

  return (
    <div className="bg-[#1a1a1a] border-b border-[#2f2f2f]">
      <div className="max-w-5xl mx-auto">
        <nav className="flex overflow-x-auto scrollbar-hide" aria-label="Tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => onTabChange(tab.id)}
              className={`flex-1 min-w-[80px] py-2 sm:py-3 px-1 sm:px-2 border-b-2 text-[11px] sm:text-sm font-medium text-center whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-white text-white'
                  : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-700'
              }`}
              style={{ touchAction: 'manipulation' }}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>
      
      {/* Add custom styles for hiding scrollbar but allowing scroll */}
      <style jsx global>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;  /* IE and Edge */
          scrollbar-width: none;  /* Firefox */
        }
      `}</style>
    </div>
  );
}